import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { setPulseWorkbenchIntent } from "@/lib/pulse-workbench-intent";
import { format } from "date-fns";

// Task #48: admin overview of old email pulses that still have no linked source email.
// Task #49: clicking a row jumps straight to the project and opens the pulse's
// workbench dialog, where "Link source email…" sits in place of the participants.

type UnlinkedPulse = {
  id: number;
  projectId: string;
  description: string;
  dateAdded: string;
};

export function UnlinkedEmailPulsesDialog({ open, onOpenChange, onNavigateToPulse }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when a row is clicked so ancestors (e.g. the settings modal) can close. */
  onNavigateToPulse?: () => void;
}) {
  const [, navigate] = useLocation();

  const { data, isLoading } = useQuery<{ count: number; pulses: UnlinkedPulse[] }>({
    queryKey: ['/api/email-pulses/unlinked'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/email-pulses/unlinked');
      if (!res.ok) throw new Error('Failed to fetch unlinked email pulses');
      return res.json();
    },
    enabled: open,
  });

  const pulses = data?.pulses || [];

  const handleOpenPulse = (pulse: UnlinkedPulse) => {
    // Close this dialog and any ancestor overlay (settings modal) first so
    // the pulse workbench isn't stacked underneath them.
    onOpenChange(false);
    onNavigateToPulse?.();

    // Same focus pipeline as quick-search: land on the dashboard with the
    // project focused, then open the pulse workbench. The durable intent
    // (consumed by ProjectTree on mount) guarantees the workbench opens even
    // when the tree mounts slower than one frame (cold start, cross-route
    // navigation from an admin page); the rAF-deferred event below is only
    // the fast path for an already-mounted tree, which also consumes the
    // intent so it can't apply twice.
    setPulseWorkbenchIntent({ pulseId: pulse.id });
    navigate(`/?projectId=${encodeURIComponent(pulse.projectId)}`);
    window.dispatchEvent(
      new CustomEvent('navigate-to-project', { detail: { projectId: pulse.projectId } }),
    );
    requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent('open-pulse-workbench', { detail: { pulseId: pulse.id } }),
      );
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            Email pulses without source email {data ? `(${data.count})` : ''}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-400">
          These older email pulses are not yet connected to the email they came from,
          so no participants are shown on them. Click a pulse to open it in its project,
          then use “Link source email…” to connect the right email.
        </p>
        <div className="max-h-96 overflow-y-auto space-y-1" data-testid="unlinked-email-pulses-list">
          {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
          {!isLoading && pulses.length === 0 && (
            <p className="text-sm text-green-400">All email pulses are linked to their source email.</p>
          )}
          {pulses.map(p => (
            <button
              type="button"
              key={p.id}
              onClick={() => handleOpenPulse(p)}
              className="block w-full text-left rounded border border-gray-700 bg-gray-900/40 px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-700/60 hover:border-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-colors"
              title="Open this pulse in its project"
              data-testid={`unlinked-pulse-${p.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-blue-300 shrink-0">{p.projectId}</span>
                <span className="text-gray-400 shrink-0">{format(new Date(p.dateAdded), 'dd.MM.yyyy HH:mm')}</span>
              </div>
              <div className="text-gray-300 truncate">{p.description || '(no description)'}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
